// Lib
import React, { ReactElement } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';

// Actions
import { redoAction, undoAction } from '../../../../utils/undoRedo/undoRedoActions';

// Utils
import { sendAmplitudeEvent } from '../../../../analytics/amplitudeServiceJS';
import { isPlatformApple } from '../../../../platform/utils/platformDetailsUtils';

// Selectors
import { getRedoStack, getUndoStack } from '../../../../utils/undoRedo/undoRedoSelector';
import { getPlatformDetailsSelector } from '../../../../platform/platformSelector';

// Components
import Icon from '../../../../components/icons/Icon';
import Button from '../../../../components/buttons/Button';
import TooltipSource from '../../../../components/tooltips/TooltipSource';

// Constants
import { EVENT_TYPE_NAMES } from '../../../../../common/analytics/amplitudeEventTypesUtil';

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchUndoAction: () => dispatch(undoAction()),
    dispatchRedoAction: () => dispatch(redoAction()),
});

const mapStateToProps = createStructuredSelector({
    undoStack: getUndoStack,
    redoStack: getRedoStack,
    platformDetails: getPlatformDetailsSelector,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & {
    children: React.ReactElement | string;
    tooltipEnabled: boolean;
};

const UndoRedoUtil = ({
    dispatchUndoAction,
    dispatchRedoAction,
    undoStack,
    redoStack,
    tooltipEnabled,
    platformDetails,
}: Props): ReactElement | null => {
    const hasUndo = undoStack.size > 0;
    const hasRedo = redoStack.size > 0;

    const onUndo = () => {
        dispatchUndoAction();

        sendAmplitudeEvent({
            eventType: EVENT_TYPE_NAMES.CLICKED_UNDO_BUTTON,
        });
    };

    const onRedo = () => {
        dispatchRedoAction();

        sendAmplitudeEvent({
            eventType: EVENT_TYPE_NAMES.CLICKED_REDO_BUTTON,
        });
    };

    return (
        <div className="HeaderUtil UndoRedoUtil">
            <TooltipSource
                tooltipText="Undo"
                shortcut={['command', 'z']}
                distance={0}
                delay={700}
                enabled={tooltipEnabled}
                triggerOnMouseEnter
            >
                <Button className={classNames('header-util-button', { disabled: !hasUndo })} onClick={onUndo}>
                    <div className="header-util-button-content">
                        <Icon name="nav-undo" />
                    </div>
                </Button>
            </TooltipSource>
            <TooltipSource
                tooltipText="Redo"
                distance={0}
                shortcut={isPlatformApple(platformDetails) ? ['command', 'shift', 'z'] : ['ctrl', 'y']}
                delay={700}
                enabled={tooltipEnabled}
                triggerOnMouseEnter
            >
                <Button className={classNames('header-util-button', { disabled: !hasRedo })} onClick={onRedo}>
                    <div className="header-util-button-content">
                        <Icon name="nav-redo" />
                    </div>
                </Button>
            </TooltipSource>
        </div>
    );
};

export default connector(UndoRedoUtil);
